import BgLogin from "$root/assets/images/bg_login.jpg";
import SPHERE_Logo from "$root/assets/images/SPHERE_logo.svg";
import { useUserValue } from "$root/functional-areas/user";
import { authSessionInit } from "$root/functional-areas/user/auth";
import { beginSignin } from "$root/functional-areas/user/oidc";
import { FormController, rejectOnInvalid } from "$root/third-party-integrations/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { TextInput, Form, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router";
import { z } from "zod";

export type LoginProps = {
	from?: string;
	atubm?: string;
};

const loginSchema = z.object({
	email: z.string().min(1, "Please enter your email").email("Please enter a valid email"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

/**
 * Simplified SSO entry point. The user enters their email and, based on its
 * domain, is redirected to the matching AWS Cognito Hosted UI (via `oidc-client-ts`).
 * Cognito redirects back to `/auth/callback` to finish the Authorization Code + PKCE
 * exchange. See `functional-areas/user/oidc.ts` for the per-domain pool mapping.
 */
export function Login({ from: redirectTo, atubm }: LoginProps): React.JSX.Element {
	const { t } = useTranslation();
	const user = useUserValue();

	const form = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: { email: "" },
	});

	// Paperplane: a backend-issued token injected directly into the session, bypassing OIDC.
	useEffect(() => {
		if (atubm) {
			authSessionInit(atubm).catch(console.warn);
		}
	}, [atubm]);

	const handleContinue = async ({ email }: LoginFormValues) => {
		// Redirects the browser to the Cognito Hosted UI for the resolved pool.
		await beginSignin(email, redirectTo);
	};

	if (user.signedIn) {
		return <Redirect to={redirectTo || "/"} />;
	}

	return (
		<Column
			className="h-full w-full"
			style={{
				backgroundImage: `url(${BgLogin})`,
				backgroundSize: "cover",
				backgroundPosition: "bottom center",
			}}
		>
			<Column flexGrow={1} className="h-full bg-white px-20 ml-auto max-w-xl w-full overflow-y-auto">
				<div className="mt-auto pt-4 mb-32">
					<img src={SPHERE_Logo} className="w-40 h-auto" alt="Sphere Logo" />
				</div>
				<Form noValidate onSubmitAsync={rejectOnInvalid(form, handleContinue)}>
					<Text as="div" type="Title/L/Light">
						Welcome to sphere
					</Text>
					<Text as="div" type="Body/L/Book" className="mt-6 mb-7">
						Enter your email to continue
					</Text>
					<Column gap={24}>
						<FormField label={null} error={form.formState.errors.email?.message} className="w-full">
							{({ id, invalid }) => (
								<FormController
									control={form.control}
									name="email"
									render={({ field: { onChange, ref, ...controllerProps } }) => (
										<TextInput
											required
											autoComplete="email"
											placeholder={t("login.USERNAME_PLACEHOLDER")}
											id={id}
											invalid={invalid}
											onChangeText={onChange}
											innerRef={ref}
											data-qualifier="login-email"
											{...controllerProps}
										/>
									)}
								/>
							)}
						</FormField>
						<Row alignItems="center" flexGrow={0} justifyContent="flex-end">
							<SubmitButton data-qualifier="login-form-submit">{t("login.LOGIN")}</SubmitButton>
						</Row>
					</Column>
				</Form>
				<Column flexGrow={0} className="mt-auto pt-4 mb-5">
					<Text as="div" type="Body/M/Book" color={themeCSSVars.palette_N500}>
						<div>MDOTM Srl - Via passione 8, 20122, Milano - P.iva 09601510960 </div>
						<a
							href="https://www.iubenda.com/privacy-policy/48016693"
							target="_blank"
							rel="noreferrer noopener"
							className="iubenda-nostyle no-brand iubenda-noiframe iubenda-embed iubenda-noiframe underline"
							title="Privacy Policy "
						>
							Privacy Policy
						</a>
					</Text>
				</Column>
			</Column>
		</Column>
	);
}

export default Login;
